import React from 'react';
import { Worker } from '../types';

// Worker Create/Edit Modal
interface WorkerModalProps {
  worker: Worker | null;
  isEditing: boolean;
  onClose: () => void;
  onSave: (workerData: any) => void;
}

export const WorkerModal: React.FC<WorkerModalProps> = ({
  worker,
  isEditing,
  onClose,
  onSave
}) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '15px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <h3 style={{ marginBottom: '1.5rem', color: '#228B22' }}>
          {isEditing ? 'Edit Worker' : 'Add New Worker'}
        </h3>

        <form onSubmit={(e) => {
          e.preventDefault();
          const formData = new FormData(e.currentTarget);
          const workerData = {
            name: formData.get('name'),
            email: formData.get('email'),
            region: formData.get('region'),
            phone: formData.get('phone'),
            maxActiveOrders: parseInt(formData.get('maxActiveOrders') as string) || 5,
            ...(isEditing ? {} : { password: formData.get('password') })
          };
          onSave(workerData);
        }}>
          <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Name *
              </label>
              <input
                name="name"
                type="text"
                required
                defaultValue={worker?.name || ''}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Email *
              </label>
              <input
                name="email"
                type="email"
                required
                defaultValue={worker?.email || ''}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            {!isEditing && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Password *
                </label>
                <input
                  name="password"
                  type="password"
                  required
                  minLength={6}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Region *
              </label>
              <select
                name="region"
                required
                defaultValue={worker?.region || ''}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select Region</option>
                <option value="Amazon Basin">Amazon Basin</option>
                <option value="Atlantic Forest">Atlantic Forest</option>
                <option value="Andes Mountains">Andes Mountains</option>
                <option value="Pacific Coast">Pacific Coast</option>
                <option value="Caribbean Coast">Caribbean Coast</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Phone
              </label>
              <input
                name="phone"
                type="tel"
                defaultValue={worker?.phone || ''}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Max Active Orders
              </label>
              <input
                name="maxActiveOrders"
                type="number"
                min="1"
                max="20"
                defaultValue={worker?.maxActiveOrders || 5}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '0.75rem 1.5rem',
                background: '#228B22',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              {isEditing ? 'Update Worker' : 'Create Worker'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Worker Selection Modal (for assigning orders)
interface WorkerSelectionModalProps {
  workers: Array<{_id: string, name: string, email: string, region?: string}>;
  onSelect: (workerId: string) => void;
  onClose: () => void;
}

export const WorkerSelectionModal: React.FC<WorkerSelectionModalProps> = ({
  workers,
  onSelect,
  onClose
}) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '15px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <h3 style={{ marginBottom: '1.5rem', color: '#228B22' }}>
          Assign Worker to Order
        </h3>

        <p style={{ marginBottom: '1.5rem', color: '#666' }}>
          Select a worker to assign this order to:
        </p>

        <div style={{ marginBottom: '2rem' }}>
          {workers.length === 0 ? (
            <p style={{ color: '#dc3545', fontStyle: 'italic' }}>
              No available workers found. Please add workers first.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {workers.map(worker => (
                <div
                  key={worker._id}
                  onClick={() => onSelect(worker._id)}
                  style={{
                    padding: '1rem',
                    border: '2px solid #e9ecef',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#228B22';
                    e.currentTarget.style.background = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e9ecef';
                    e.currentTarget.style.background = 'white';
                  }}
                >
                  <div style={{ fontWeight: '600', color: '#228B22', marginBottom: '0.5rem' }}>
                    {worker.name}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>
                    📧 {worker.email}
                  </div>
                  {worker.region && (
                    <div style={{ color: '#666', fontSize: '0.9rem' }}>
                      📍 {worker.region}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
